import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { CeoInboxItem } from '../../services/company-api.js';
import * as companyApi from '../../services/company-api.js';

@customElement('cp-ceo-inbox-tab')
export class CpCeoInboxTab extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) items: CeoInboxItem[] = [];
  @property({ type: Boolean }) loading = false;

  @state() private _unblockedIds = new Set<number>();
  @state() private _expandedId: number | null = null;
  @state() private _unblockingId: number | null = null;
  @state() private _error: string | null = null;

  private async _handleUnblock(item: CeoInboxItem): Promise<void> {
    if (this._unblockingId !== null) return;
    this._unblockingId = item.id;
    this._error = null;
    try {
      await companyApi.unblockStrategy(item.id);
      this._unblockedIds = new Set([...this._unblockedIds, item.id]);
      this.dispatchEvent(new CustomEvent('ceo-inbox-reload', { bubbles: true, composed: true }));
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Entsperren';
    } finally {
      this._unblockingId = null;
    }
  }

  private _toggleExpanded(id: number): void {
    this._expandedId = this._expandedId === id ? null : id;
  }

  private _stateLabel(state: string): string {
    if (state === 'blocked') return 'Blockiert';
    if (state === 'executing') return 'In Arbeit';
    return state;
  }

  private _renderItem(item: CeoInboxItem) {
    const expanded = this._expandedId === item.id;
    const isUnblocking = this._unblockingId === item.id;
    const alreadyUnblocked = this._unblockedIds.has(item.id);
    const isBlocked = item.state === 'blocked';

    return html`
      <div class="cp-ceo-inbox-tab__item cp-ceo-inbox-tab__item--${item.state}">
        <div class="cp-ceo-inbox-tab__item-header">
          <div class="cp-ceo-inbox-tab__item-meta">
            <span class="cp-ceo-inbox-tab__state-badge cp-ceo-inbox-tab__state-badge--${item.state}">
              ${this._stateLabel(item.state)}
            </span>
            ${item.auto_escalated ? html`<span class="cp-ceo-inbox-tab__escalated-badge">Auto-Eskaliert</span>` : nothing}
          </div>
          <div class="cp-ceo-inbox-tab__item-title">${item.goal_title}</div>
          <div class="cp-ceo-inbox-tab__item-agent">${item.agent_name} &middot; ${item.company_name}</div>
        </div>
        ${item.block_reason ? html`
          <div class="cp-ceo-inbox-tab__block-reason">${item.block_reason}</div>
        ` : nothing}
        <div class="cp-ceo-inbox-tab__item-actions">
          ${isBlocked && !alreadyUnblocked ? html`
            <button
              class="cp-ceo-inbox-tab__btn cp-ceo-inbox-tab__btn--unblock"
              ?disabled=${isUnblocking}
              @click=${() => void this._handleUnblock(item)}
            >${isUnblocking ? 'Entsperre...' : 'Entsperren'}</button>
          ` : nothing}
          ${alreadyUnblocked ? html`
            <span class="cp-ceo-inbox-tab__unblocked-label">Entsperrt</span>
          ` : nothing}
          <button
            class="cp-ceo-inbox-tab__btn ${expanded ? 'cp-ceo-inbox-tab__btn--details-active' : ''}"
            @click=${() => this._toggleExpanded(item.id)}
          >${expanded ? 'Weniger' : 'Details'}</button>
        </div>
        ${expanded ? html`
          <div class="cp-ceo-inbox-tab__details">
            <dl class="cp-ceo-inbox-tab__details-list">
              <dt>Typ</dt><dd>${item.delegation_type === 'task' ? 'Task' : 'Ziel'}</dd>
              <dt>Erstellt</dt><dd>${new Date(item.created_at).toLocaleString('de-DE')}</dd>
              <dt>Zuletzt aktualisiert</dt><dd>${new Date(item.updated_at).toLocaleString('de-DE')}</dd>
              ${item.block_reason ? html`<dt>Blockierungsgrund</dt><dd>${item.block_reason}</dd>` : nothing}
            </dl>
          </div>
        ` : nothing}
      </div>
    `;
  }

  render() {
    if (this.loading && this.items.length === 0) {
      return html`<div class="cp-ceo-inbox-tab__loading">Lade CEO-Inbox...</div>`;
    }

    if (this.items.length === 0) {
      return html`
        <div class="cp-ceo-inbox-tab__empty">
          <p class="cp-ceo-inbox-tab__empty-text">Keine offenen Delegationen</p>
          <p class="cp-ceo-inbox-tab__empty-sub">Geblockte oder veraltete CEO-Delegationen erscheinen hier automatisch.</p>
        </div>
      `;
    }

    return html`
      ${this._error ? html`<div class="cp-ceo-inbox-tab__error">${this._error}</div>` : nothing}
      <div class="cp-ceo-inbox-tab__list">
        ${this.items.map(item => this._renderItem(item))}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-ceo-inbox-tab': CpCeoInboxTab;
  }
}
